import { useDrawerStore } from '@nexelem/drawer';
import { IconButton, Button, Status } from '@nexelem/components';
import { useDateFormatter } from '@nexelem/utils';
import { TaskView } from '@nexelem/graphne-api';
import { TaskTypeIcon } from './TaskTypeIcon';
import { GanttUtils } from '../../../../../../components/organisms/ProductionPlans/utils';
import { useCustomTranslation } from '../../../../../../hooks';
import {
  AVAILABLE_QUERY_PARAMS,
  useProductionPlanQueryParams,
} from '../../../../../../components/organisms/ProductionPlans/hooks';
import {
  TaskMainValueOptions,
  useProductionPlanSettings,
} from '../../../../../../components/organisms/ProductionPlans/components/SettingsDrawer';
import { toggleProductionPlanTasksDrawer } from '../../../../../../components/organisms/ProductionPlans/components/TasksDrawer/index';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faGear, faClock, faArrowLeft, faCalendarClock } from '@fortawesome/pro-light-svg-icons';
import { faFlag } from '@fortawesome/pro-solid-svg-icons';
import { formatUnit } from '../../../../../../components/organisms/ProductionPlans/Kanban/components/KanbanTask';

type TaskDetailsDrawerHeaderProps = {
  task: TaskView;
  hasBackButton: boolean;
};

export const TaskDetailsDrawerHeader = ({ task, hasBackButton }: TaskDetailsDrawerHeaderProps) => {
  const taskMainValue = useProductionPlanSettings((state) => state.taskMainValue);
  const statusTranslation = GanttUtils.useTaskStatusTranslation();

  const { tWrapper } = useCustomTranslation('products.productionPlan.taskDetailsDrawer');
  const { tWrapper: taskTypeTranslation } = useCustomTranslation('products.productionPlan.taskType');

  const formatterWithHours = useDateFormatter({ displayWithHours: true });

  const onCloseDrawer = useDrawerStore((state) => state.onCloseDrawer);
  const drawerName = useDrawerStore((state) => state.left.name);
  const { setQueryParams } = useProductionPlanQueryParams();

  const handleCloseDetailsDrawer = () => {
    setQueryParams([
      { key: AVAILABLE_QUERY_PARAMS.queueId, value: undefined },
      { key: AVAILABLE_QUERY_PARAMS.taskId, value: undefined },
      { key: AVAILABLE_QUERY_PARAMS.taskExecutionId, value: undefined },
    ]);

    onCloseDrawer(drawerName);
  };

  const { name, taskType, productDetails, status, dueDate, setupEnd, errors, progress } = task;
  const { current, totalAmount } = progress;
  const isOverdue = GanttUtils.hasOverdueError(task.errors);

  const formattedCurrent = formatUnit(current);
  const formattedTotal = formatUnit(totalAmount.amount);
  const statusTranslated = statusTranslation(status, errors);
  const statusText = `${formattedCurrent}/${formattedTotal} ${totalAmount.unit.name} ${statusTranslated}`;

  const mainField = taskMainValue === TaskMainValueOptions.PRODUCT ? productDetails.name : name;
  const secondaryField = taskMainValue === TaskMainValueOptions.PRODUCT ? name : productDetails.name;

  return (
    <div className="flex flex-col items-start py-4">
      {hasBackButton && (
        <Button
          className="mb-2.5 ml-2"
          StartIcon={<FontAwesomeIcon icon={faArrowLeft} />}
          variant="text"
          onClick={toggleProductionPlanTasksDrawer}
        >
          {`${tWrapper('backTo', { backElement: tWrapper('tasks') })}`}
        </Button>
      )}
      <div className="flex flex-col px-6">
        <span className="pr-7 pb-1 text-t24-sb">{mainField}</span>
        <div className="flex items-center gap-2 pb-2">
          <TaskTypeIcon className="min-h-6 w-6 text-icon-primary" taskType={taskType} />
          <span className="text-s16 text-text-secondary">
            {tWrapper('productLabel', { taskType: taskTypeTranslation(taskType), productName: secondaryField })}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {task.priority && (
            <Status
              className="pl-2"
              color="green"
              Icon={<FontAwesomeIcon className="text-icon-brand" icon={faFlag} />}
              text={tWrapper('priority')}
              variant="contained"
            />
          )}
          {isOverdue && (
            <Status
              className="pl-2"
              color="rose"
              Icon={<FontAwesomeIcon className="text-icon-status-rose" icon={faClock} />}
              text={tWrapper('overdue')}
              variant="contained"
            />
          )}
          <Status color={GanttUtils.getTaskStatusColor(status, errors)} text={statusText} variant="outlined" />
          {dueDate && (
            <Status
              className="pl-2"
              color="grey"
              Icon={<FontAwesomeIcon className="text-icon-primary" icon={faCalendarClock} />}
              text={tWrapper('dueDate', { dueDate: formatterWithHours(new Date(dueDate)) })}
              variant="outlined"
            />
          )}
          {!!setupEnd && (
            <Status
              className="pl-2"
              color="grey"
              Icon={<FontAwesomeIcon className="text-icon-primary" icon={faGear} />}
              text={tWrapper('setupEnd')}
              variant="outlined"
            />
          )}
        </div>
      </div>
      <IconButton
        className="absolute top-4 right-2"
        Icon={<FontAwesomeIcon icon={faXmark} />}
        onClick={handleCloseDetailsDrawer}
      />
    </div>
  );
};
